import { useMemo, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { Heart } from 'lucide-react'
import { PageContainer } from 'src/shared/components/PageContainer'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { EmptyState } from 'src/shared/components/EmptyState'
import { FullScreenLoader } from 'src/shared/components/FullScreenLoader'
import { ProductGrid } from 'src/shared/components/ProductGrid'
import { useGetOrgId } from 'src/api/helpers/getOrgId'
import { useOrgFavouritesByCategoryApi } from 'src/api/db/apiOrgFavourites'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Combobox } from 'src/shared/components/Combobox'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useDebounce } from 'src/shared/hooks/useDebounce'

import { AddFavouriteByArticleDialog } from './components/AddFavouriteByArticleDialog'

const categoryHeadingT = (
  t: (en: string, sv: string) => string,
  categoryTitle: string | null
) => {
  const raw = categoryTitle ?? ''
  const parts = raw.includes(',')
    ? raw.split(',').map((s) => s.trim())
    : raw.split(' || ').map((s) => s.trim())
  return t(parts[0] ?? '', parts[1] ?? '')
}

type FavouritesPageProps = {
  embedded?: boolean
}

export const FavouritesPage = (props: FavouritesPageProps = {}) => {
  const { embedded = false } = props
  const { t } = useLocalization()
  const isAdmin = useIsAdmin()
  const { org_id: routeOrgId } = useParams<{ org_id?: string }>()
  const org_id = useGetOrgId()
  const [selectedOrg, setSelectedOrg] = useState('')
  const [orgSearchInput, setOrgSearchInput] = useState('')
  const debouncedOrgSearch = useDebounce(orgSearchInput, 300)

  const effectiveOrgId = isAdmin ? selectedOrg || org_id : org_id
  const showAdminOrgPicker = isAdmin && !routeOrgId

  const { useSearchOrganizations, organizationById } = useOrgsApi({
    org_id: effectiveOrgId ?? undefined,
  })
  const { data: searchedOrgs } = useSearchOrganizations({
    search: debouncedOrgSearch,
    limit: 20,
  })

  const orgOptions = useMemo(
    () =>
      (searchedOrgs?.data ?? [])
        .filter((org) => org.name !== 'Anon')
        .map((org) => ({ value: org.id, label: org.name })),
    [searchedOrgs?.data]
  )

  const effectiveOrgName = useMemo(() => {
    const fromFetch = organizationById?.data?.name
    if (fromFetch) return fromFetch
    return (
      orgOptions.find((o) => o.value === effectiveOrgId)?.label ?? null
    )
  }, [organizationById?.data?.name, orgOptions, effectiveOrgId])

  const { categoryGroups, isLoading } = useOrgFavouritesByCategoryApi({
    orgId: effectiveOrgId,
  })

  const totalCount = useMemo(
    () => categoryGroups.reduce((n, g) => n + g.productIds.length, 0),
    [categoryGroups]
  )

  const showEmptyState = !effectiveOrgId || totalCount === 0

  const adminFavouritesToolbar = isAdmin ? (
    <Row className="gap-3 items-center mt-4">
      {showAdminOrgPicker && (
        <Column className="w-[190px] mb-0">
          <Combobox
            ariaLabel={t('Organization filter', 'Organisationsfilter')}
            value={selectedOrg}
            onValueChange={(next) => setSelectedOrg(next)}
            options={orgOptions}
            onSearchChange={setOrgSearchInput}
            placeholder={t(
              'Filter by organization...',
              'Filtrera efter organisation...'
            )}
            searchPlaceholder={t('Search organization...', 'Sök organisation...')}
            emptyMessage={t(
              'No organizations found.',
              'Inga organisationer hittades.'
            )}
            clearOnReselect
            buttonVariant="secondary"
            buttonClassName="font-normal"
          />
        </Column>
      )}
      <AddFavouriteByArticleDialog
        orgId={effectiveOrgId}
        orgName={effectiveOrgName}
      />
    </Row>
  ) : null

  const shell = (children: ReactNode) =>
    embedded ? (
      <Column className="gap-4">{children}</Column>
    ) : (
      <PageContainer className="gap-4">{children}</PageContainer>
    )

  if (isLoading) {
    return <FullScreenLoader />
  }

  if (showEmptyState) {
    return shell(
      <Column className="gap-4">
        <Row className="items-center gap-2">
          <Heart className="h-5 w-5" />
          <h1>{t('Favourites', 'Favoriter')}</h1>
        </Row>

        {adminFavouritesToolbar}

        <EmptyState
          icon={<Heart className="w-9 h-9" />}
          title={t('No favourites yet', 'Inga favoriter ännu')}
          description={t(
            'View a product, then click "Add to favorites"',
            'Visa en produkt, klicka sedan på "Lägg till i favoriter"'
          )}
          image="https://p58.f1.n0.cdn.zight.com/items/bLu9RloG/d535d946-3257-4d72-8ad2-21d9d6647c8b.jpg?source=viewer&v=9ac9e61693b5e09d3063207916d76878"
        />
      </Column>
    )
  }

  return shell(
    <Column>
      <Row className="items-center gap-2">
        <Heart className="h-5 w-5" />
        <h1>{t('Favourites', 'Favoriter')}</h1>
      </Row>

      <Row className="items-center justify-between">
        <p className="text-sm md:text-base text-gray-600">
          {t(
            'Products your organization has saved as favourites',
            'Produkter din organisation har sparat som favoriter'
          )}
        </p>
        {adminFavouritesToolbar}
      </Row>

      <Column className="gap-10 mt-4">
        {categoryGroups.map((group) => {
          const product_items = group.productIds.map((product_id) => ({
            product_id,
          }))

          const heading =
            group.categoryId === -1
              ? t('Other', 'Övrigt')
              : categoryHeadingT(t, group.categoryTitle) ||
                t('Uncategorized', 'Okategoriserad')

          return (
            <Column key={group.categoryId} className="gap-4">
              <h3>{heading}</h3>
              <ProductGrid
                product_items={product_items}
                scrollArea={false}
                showAddToQuote={true}
                favouritesOrgId={
                  isAdmin ? effectiveOrgId ?? undefined : undefined
                }
              />
            </Column>
          )
        })}
      </Column>
    </Column>
  )
}
